<script lang="ts">
    import type {Exemplar, ExtendedDocument, RichDocument} from '../../typings/portaro.be.types';
    import type {Injector, LocalizeFunction} from '../../svelte/types';
    import {INJECTOR_KEY, LOCALIZE_KEY, pipe} from '../../svelte/utils';
    import {KpDocumentMiniPresenter} from './kp-document-mini.presenter';
    import KpCover from '../kp-cover/KpCover.svelte';
    import {extendDocument} from '../../shared/utils/document-utils';
    import {isExtendedDocument} from '../../shared/utils/types-utils';
    import {characters, inline, loc, prop} from '../../svelte/pipes';
    import {getContext} from 'svelte';

    export let document: RichDocument | ExtendedDocument;
    export let exemplar: Exemplar  = null;

    const injector: Injector = getContext(INJECTOR_KEY)
    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);

    const presenter: KpDocumentMiniPresenter = injector.getByToken(KpDocumentMiniPresenter.presenterName);

    const extendedDocument = isExtendedDocument(document) ? document : extendDocument(document as ExtendedDocument)

    if (exemplar && presenter.isExemplarVolumeMissing(exemplar)) {
        presenter.appendVolume(exemplar)
    }

    const seriesText = presenter.getSeriesText(extendedDocument);
    const sf245c = presenter.getSf245c(extendedDocument);
    const sfAltAuthors =  presenter.getSfAltAuthors(sf245c, extendedDocument);
</script>

<template>
    <div class="document-mini-container">
        <div class="cover-container cover-container-document-mini">
            <KpCover record={extendedDocument} fillTo="width"/>
        </div>
        <div class="document-mini-content">
            {#if !extendedDocument.id}
                <span>{extendedDocument.name}</span>
            {:else}
                <a href={presenter.getDocumentLink(extendedDocument)}>{extendedDocument.name}</a>
            {/if}

            {#if !!extendedDocument.fields}
                <div class="supplementaryText">{extendedDocument.subTitle}</div>
                {#if seriesText}
                    <div class="supplementaryText">{seriesText}</div><!-- serie -->
                {/if}
                {#if !!sf245c}
                    <div class="supplementaryText">{pipe(sf245c, prop('text'), inline())}</div>
                {/if}
                {#if !!sfAltAuthors}
                    <div class="supplementaryText">{pipe(sfAltAuthors, prop('text'), inline())}</div>
                {/if}
                <div class="supplementaryText">{pipe(extendedDocument.getSubfields('260', 'abc'), prop('text'), inline(' '))}</div><!-- Nakladatelské údaje -->
                <div class="supplementaryText">{pipe(extendedDocument.getSubfields('264', 'abc'), prop('text'), inline(' '))}</div><!-- Nakladatelské údaje RDA -->
                {#if !exemplar?.signature}
                    <div class="supplementaryText">{pipe(extendedDocument.getSubfields('910', 'b'), prop('text'), inline(), characters(30))}</div><!-- signatura -->
                {/if}
                <div class="supplementaryText">{pipe(extendedDocument.getSubfields('773'), prop('text'), inline())}</div><!-- zdrojovy dokument -->
            {/if}

            {#if !!exemplar}
                <!-- div to encapsulate inline <span> elements in block element -->
                <div>
                    {#if !!exemplar.signature}
                        <span>{localize(/* @kp-localization exemplar.signature */ 'exemplar.signature')}: {exemplar.signature}</span>
                    {/if}
                    {#if !!exemplar.barCode}
                        <span>{localize(/* @kp-localization exemplar.barCode */ 'exemplar.barCode')}: {exemplar.barCode}</span>
                    {/if}
                    {#if (!exemplar.signature && !exemplar.barCode && !!exemplar.accessNumber)}
                        <!-- access number zobrazime jen pokud exemplar nema signaturu ani BC -->
                        <span>{localize(/* @kp-localization exemplar.accessNumber */ 'exemplar.accessNumber')}: {exemplar.accessNumber}</span>
                    {/if}
                    {#if !!exemplar.volume}
                    <span>
                        {localize(/* @kp-localization commons.rocnik */ 'commons.rocnik')}: {pipe(exemplar.volume, loc())}
                        {#if !!exemplar.volume.year}
                            <span>({exemplar.volume.year})</span>
                        {/if}
                    </span>
                    {/if}
                    {#if !!exemplar.bundledVolumeNumber}
                        <span>{localize(/* @kp-localization exemplar.bundledVolumeNumber */ 'exemplar.bundledVolumeNumber')}: {exemplar.bundledVolumeNumber}</span>
                    {/if}
                    {#if !!exemplar.bundledVolumeYear}
                        <span>{localize(/* @kp-localization exemplar.bundledVolumeYear */ 'exemplar.bundledVolumeYear')}: {exemplar.bundledVolumeYear}</span>
                    {/if}
                    {#if !!exemplar.bundledVolumeIssueRange}
                        <span>{localize(/* @kp-localization exemplar.bundledVolumeIssueRange */ 'exemplar.bundledVolumeIssueRange')}: {exemplar.bundledVolumeIssueRange}</span>
                    {/if}
                    {#if !!exemplar.issueName}
                        <span>{localize(/* @kp-localization exemplar.issueName */ 'exemplar.issueName')}: {exemplar.issueName}</span>
                    {/if}
                </div>
            {/if}
        </div>
    </div>
</template>

<style lang="less">
    .document-mini-container {
      display: flex;
      flex-direction: row;
    }

    .cover-container-document-mini {
        width: 31px;
        flex-shrink: 0;
    }

    .document-mini-content {
        margin-left: 9px;
    }

    .supplementaryText {
        color: gray
    }
</style>